import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaInfo as InfoIcon,
  FaRegStar as FavouriteIconDeselected,
  FaStar as FavouriteIconSelected,
} from 'react-icons/fa';
import { generatePath } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import { DatalineType } from 'src/constants/dataline';
import {
  FILE_TYPE_MAP,
  REPORT_TYPE_MAP,
  ReportOrderStatus,
} from 'src/constants/report';
import type { ReportDatalineWrapper } from 'src/types/dataline';
import type { TemplateOrReportItem } from 'src/types/workspace';
import { formatDateString, getOrderStatus } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import Modal from 'src/components/common/Modal';
import { getConfigFromMap } from 'src/components/reportConfig/helper';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

export const WORKSPACE_INFO_MODAL = 'workspace-info-modal';
export const WORKSPACE_INFO_MODAL_FAVORITE_TEST_ID =
  'workspace-info-modal-favourite';

export interface ItemInfoModalProps {
  headline?: string;
  item: TemplateOrReportItem;
  isOpen?: boolean;
  onClose: () => any;
  showFavouriteButton?: boolean;
  reportNamesPerReportType: { [p: string]: string };
  abbreviationPerFileType: { [p: string]: string };
  loadingReportConfigData: boolean;
}

const ItemInfoModal: FC<ItemInfoModalProps> = ({
  headline,
  item,
  isOpen = false,
  onClose,
  showFavouriteButton = true,
  reportNamesPerReportType,
  abbreviationPerFileType,
  loadingReportConfigData,
}) => {
  const { t } = useTranslation('report');
  const {
    getCustomReports,
    getDatalineDefinitionFromBackendObject,
    getDatalineTemplates,
  } = useWorkspaceContext();
  const [isFavourite, setIsFavourite] = useState(item.is_favourite);

  const isReport = 'report_type' in item;

  const {
    createdAt,
    fileType,
    orderedAt,
    orderStatus,
    reportType,
    favouriteEndpoint,
    isGlobalTemplate,
    createdBy,
  } = useMemo(() => {
    const favouriteEndpointValue = isReport
      ? apiPaths.REPORTS.SET_FAVORITE
      : apiPaths.TEMPLATE_SET_FAVORITE;
    const createdByValue =
      !isReport && 'dataline' in item && item.dataline?.created_by
        ? item.dataline.created_by
        : item.created_by;
    const reportTypeId = 'report_type' in item ? item.report_type : undefined;
    const reportType = getConfigFromMap(
      reportTypeId,
      reportNamesPerReportType,
      REPORT_TYPE_MAP,
    );
    const fileTypeId = 'file_type' in item ? item.file_type : undefined;
    const fileType = getConfigFromMap(
      fileTypeId,
      abbreviationPerFileType,
      FILE_TYPE_MAP,
    );

    return {
      createdAt: item.created_at ? formatDateString(item.created_at) : null,
      fileType: fileType,
      orderedAt:
        'ordered_at' in item && item.ordered_at
          ? formatDateString(item.ordered_at)
          : null,
      orderStatus:
        'order_status' in item ? getOrderStatus(item.order_status) : undefined,
      reportType: reportType,
      favouriteEndpoint: favouriteEndpointValue,
      isGlobalTemplate:
        isReport || !('is_global_template' in item)
          ? false
          : item.is_global_template,
      createdBy: createdByValue,
    };
  }, [isReport, item, reportNamesPerReportType, t]);

  const updateCustomItemList = useCallback(() => {
    if (isReport) getCustomReports({ reset: true });
    else getDatalineTemplates(true);
  }, [isReport, getCustomReports, getDatalineTemplates]);

  const toggleFavourite = useCallback(() => {
    const requestEndpoint = generatePath(favouriteEndpoint, { id: item.id });
    const payload = JSON.stringify({ is_favourite: !isFavourite });

    return HttpService.patch(requestEndpoint, payload).then(() => {
      updateCustomItemList();
      setIsFavourite(!isFavourite);
    });
  }, [favouriteEndpoint, item.id, isFavourite, updateCustomItemList]);

  return (
    <Modal
      headline={headline}
      icon={<InfoIcon size={23} />}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Table size="small" sx={styles.table} data-test-id={WORKSPACE_INFO_MODAL}>
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography sx={styles.label}>{t('reportInfo.name')}:</Typography>
            </TableCell>
            <TableCell>{item.name || '-'}</TableCell>
          </TableRow>

          {!isReport &&
            item.dataline.dataline_type !== DatalineType.EXTERNAL && (
              <TableRow>
                <TableCell>
                  <Typography sx={styles.label}>
                    {t('reportInfo.definition')}:
                  </Typography>
                </TableCell>

                <TableCell>
                  {getDatalineDefinitionFromBackendObject(
                    item as unknown as ReportDatalineWrapper,
                  )}
                </TableCell>
              </TableRow>
            )}

          {isReport && item.order_status !== ReportOrderStatus.DRAFT && (
            <>
              <TableRow>
                <TableCell>
                  <Typography sx={styles.label}>ID:</Typography>
                </TableCell>
                <TableCell>{item.id || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography sx={styles.label}>
                    {t('reportInfo.type')}:
                  </Typography>
                </TableCell>
                <TableCell>
                  {loadingReportConfigData ? (
                    <CircularProgress size={15} />
                  ) : (
                    reportType
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography sx={styles.label}>
                    {t('reportInfo.fileType')}
                  </Typography>
                </TableCell>
                <TableCell>
                  {loadingReportConfigData ? (
                    <CircularProgress size={15} />
                  ) : (
                    fileType
                  )}
                </TableCell>
              </TableRow>
            </>
          )}

          {isReport && (
            <TableRow>
              <TableCell>
                <Typography sx={styles.label}>
                  {t('reportInfo.orderStatus')}:
                </Typography>
              </TableCell>
              <TableCell>{orderStatus || '-'}</TableCell>
            </TableRow>
          )}

          {/* order and creation date are identical -> we only need to show orderedAt */}
          {'ordered_at' in item && item.ordered_at ? (
            <TableRow>
              <TableCell>
                <Typography sx={styles.label}>
                  {t('reportInfo.orderDate')}:
                </Typography>
              </TableCell>
              <TableCell>{orderedAt}</TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell>
                <Typography sx={styles.label}>
                  {t('reportInfo.creationDate')}:
                </Typography>
              </TableCell>
              <TableCell>{createdAt}</TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell>
              <Typography sx={styles.label}>
                {t('reportInfo.createdBy')}:
              </Typography>
            </TableCell>
            <TableCell>
              {createdBy
                ? `${createdBy.first_name || '-'} ${createdBy.last_name || '-'}`
                : '-'}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography sx={styles.label}>
                {t('reportInfo.sharedBy')}:
              </Typography>
            </TableCell>
            <TableCell>
              {item.shared_by
                ? `${item.shared_by.first_name || '-'} ${
                    item.shared_by.last_name || '-'
                  }`
                : '-'}
            </TableCell>
          </TableRow>

          {!isGlobalTemplate && showFavouriteButton && (
            <TableRow>
              <TableCell>
                <Typography sx={styles.label}>
                  {t('reportInfo.markAsFavourite')}:
                </Typography>
              </TableCell>
              <TableCell>
                <Box
                  sx={{
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-1.115rem',
                      left: '-0.75rem',
                    }}
                  >
                    <IconButton
                      onClick={() => toggleFavourite()}
                      data-test-id={WORKSPACE_INFO_MODAL_FAVORITE_TEST_ID}
                    >
                      {isFavourite ? (
                        <FavouriteIconSelected size={18} />
                      ) : (
                        <FavouriteIconDeselected size={18} />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Modal>
  );
};

export default ItemInfoModal;
